import React, { useMemo, useState } from "react";
import { RouteComponentProps } from "react-router";
import UserFollowUpAnalytics from "../components/mads/UserFollowUpAnalytics";
import UserFollowUpContracts from "../components/mads/UserFollowUpContracts";
import UserFollowUpReports from "../components/mads/UserFollowUpReports";
import {
  Container,
  EmptyState,
  ScreenContainer,
  Link as WLink,
  H4,
  Button,
  AvatarGroup,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tooltip,
  ScreenHeader,
} from "../components/wombat-ui";
import useGetMadFull from "../graphql/hooks/queries/useGetMadFull";
import useTranslations from "../hooks/useTranslations";
import { isUnauthorized } from "../utils/errors";

import { MadFullFragment } from "../graphql/fragments";
import { sortBy } from "lodash";
import { format } from "date-fns";
import {
  CalendarIcon,
  ClockIcon,
  InformationIcon,
  MadIcon,
  WarningIcon,
} from "../components/icons";
import UserFollowUpDocuments from "../components/mads/UserFollowUpDocuments";
import UserFollowUpActivities from "../components/mads/UserFollowUpActivities";
import {
  extractReportAnswersType,
  getLastReport,
  getOverdueReports,
  getReferentGradeAverage,
  getReportStatus,
} from "../utils/madUtils";
import useViewer from "../hooks/useViewer";

type EvaluatedSkillsProps = {
  mad: MadFullFragment;
};
const EvaluatedSkills: React.FC<EvaluatedSkillsProps> = ({ mad }) => {
  const t = useTranslations();
  const [seeAll, setSeeAll] = useState(false);
  const skillsAverage = useMemo(() => {
    const lastReport = getLastReport(mad);
    return sortBy(
      getReferentGradeAverage(
        extractReportAnswersType([lastReport], "TemplateQuestionSkill"),
        mad.referents.map((r) => r.id)
      ),
      "average",
      "asc"
    );
  }, [mad]);

  if (skillsAverage.length === 0) return null;

  return (
    <div>
      <div className="flex items-center justify-between flex-grow">
        <H4>{t("mads.assessedSkills")}</H4>
        {skillsAverage.length > 3 && (
          <Button variant="subtle-link" onClick={() => setSeeAll(!seeAll)}>
            {seeAll ? t("mads.seeLess") : t("mads.seeAll")}
          </Button>
        )}
      </div>
      <div className="mt-4 overflow-hidden bg-white shadow sm:rounded-md">
        <ul className="flex flex-col divide-y divide-gray-200">
          {skillsAverage.map(
            (
              {
                reportAnswers,
                averageGrade,
                averageGradeLabel,
                averageTimeSpent,
              },
              index
            ) => {
              const skill = reportAnswers?.[0]?.templateQuestion.subject;

              if (!seeAll && index >= 3) return null;
              return (
                <li key={skill.id}>
                  <div className="block bg-white">
                    <div className="flex items-center px-4 py-4 sm:px-6">
                      <div className="flex-1 min-w-0 sm:flex sm:items-center sm:justify-between">
                        <div>
                          <div className="flex text-sm font-medium truncate text-primary-500">
                            <p>{averageGradeLabel}</p>
                            <p className="ml-1 font-normal text-dark-100">
                              {t("mads.inSkill", { skill: skill.name })}
                            </p>
                            <p className="ml-1 font-normal text-light-900">
                              ({averageGrade})
                            </p>
                          </div>
                          <div className="flex mt-2">
                            <div className="flex items-center text-sm text-light-900">
                              <CalendarIcon iconClassName="flex-shrink-0 mr-1.5 h-5 w-5 text-light-900" />
                              <p>
                                {t("mads.lastNoteOn", {
                                  time: (
                                    <time
                                      dateTime={format(
                                        new Date(reportAnswers?.[0]?.updatedAt),
                                        t("dateFormat") as string
                                      )}
                                    >
                                      {format(
                                        new Date(reportAnswers?.[0]?.updatedAt),
                                        t("dateFormat") as string
                                      )}
                                    </time>
                                  ),
                                })}
                              </p>
                            </div>
                          </div>
                        </div>
                        {!!averageTimeSpent && (
                          <div>
                            <div className="flex text-sm truncate text-dark-100">
                              <p>{t("mads.timeSpent")}</p>
                            </div>
                            <div className="flex mt-2">
                              <div className="flex items-center text-sm text-light-900">
                                <ClockIcon iconClassName="flex-shrink-0 mr-1.5 h-5 w-5 text-light-900" />
                                <p>{averageTimeSpent} h</p>
                              </div>
                            </div>
                          </div>
                        )}
                        <div>
                          <div className="flex text-sm truncate text-dark-100">
                            <p>{t("mads.referents", { count: 0 })}</p>
                          </div>
                          <div className="flex justify-end mt-2">
                            <div className="flex items-center text-sm text-light-900">
                              <Tooltip label={"Evalué par"}>
                                <div>
                                  <AvatarGroup
                                    items={reportAnswers.map((ra) => ({
                                      src: ra.user.avatar,
                                      alt: ra.user.name,
                                      name: ra.user.name,
                                      type: "user",
                                    }))}
                                  />
                                </div>
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            }
          )}
        </ul>
      </div>
    </div>
  );
};

type UserFollowUpOverdueAlertProps = {
  mad: MadFullFragment;
};

const UserFollowUpOverdueAlert: React.FC<UserFollowUpOverdueAlertProps> = ({
  mad,
}) => {
  const t = useTranslations();
  const overdues = useMemo(() => {
    return getOverdueReports(mad);
  }, [mad]);

  if (overdues.length === 0) return null;

  return (
    <div className="p-4 rounded-md bg-yellow-50">
      <div className="flex">
        <div className="flex-shrink-0">
          <WarningIcon iconClassName="h-5 w-5 text-warning-400" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-warning-800">
            {/* Check le pluriel */}
            {t("mads.lateReport")}
          </h3>
          <div className="mt-2 text-sm text-warning-700">
            <ul className="pl-5 space-y-1 list-disc">
              {overdues.map(({ reason, concernedUsers, report }) => (
                <li>
                  {reason === "talent_overdue" &&
                    concernedUsers.length > 0 &&
                    t("mads.reportOverdueTalent", {
                      talent: concernedUsers[0].name,
                      date: format(
                        new Date(report.createdAt),
                        t("dateFormat") as string
                      ),
                    })}
                  {reason === "referent_overdue" &&
                    // t('overduereferent'), count
                    concernedUsers.map((user) => user.name).join(", ")}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

type UserFollowUpStatusProps = {
  mad: MadFullFragment;
};

const UserFollowUpStatus: React.FC<UserFollowUpStatusProps> = ({ mad }) => {
  const t = useTranslations();
  const lastReport = useMemo(() => getLastReport(mad), [mad]);
  const status = useMemo(() => {
    const last = getLastReport(mad);

    return getReportStatus(mad, last);
  }, [mad]);

  return (
    <div className="flex flex-col p-4 rounded-md bg-blue-50">
      <div className="flex">
        <div className="flex-shrink-0">
          <InformationIcon iconClassName="h-5 w-5 text-primary-300" />
        </div>
        <div className="flex-1 ml-3 md:flex md:justify-between">
          <p className="text-sm text-primary-700">
            {status === "awaiting_talent" &&
              t("mads.waitingForTalent", {
                talent: mad.talent.name,
              })}
            {status === "not_started" &&
              t("mads.willStartOn", { date: mad.startDate })}
            {status === "mad_ended" && t("mads.madOverReportAvailable")}
            {status === "report_ready" && t("mads.newReportAvailable")}
            {status === "awaiting_referents" &&
              t("mads.waitingForReferent", {
                referent: `${lastReport.reportReferents
                  .filter((rr) => !rr.filledAt)
                  .map((r) => r.referent.name)
                  .join(", ")}`,
              })}
          </p>
          {mad.lastCheckedReport && (
            <p className="mt-3 text-sm md:mt-0 md:ml-6">
              <WLink
                to={`/reports/${mad.lastCheckedReport.id}`}
                className="font-medium text-primary-700 whitespace-nowrap hover:text-primary-600"
              >
                {t("mads.seeLastReport")} <span aria-hidden="true">&rarr;</span>
              </WLink>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

type UserFollowUpBasicInformations = {
  mad: MadFullFragment;
};

const UserFollowUpBasicInformations: React.FC<UserFollowUpBasicInformations> = ({
  mad,
}) => {
  const t = useTranslations();

  return (
    <div className="overflow-hidden bg-white">
      <H4>{t("mads.followUpInfo")}</H4>
      <div className="px-4 py-5 sm:px-6">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-light-900">
              {t("mads.talent")}
            </dt>
            <dd className="mt-1 text-sm text-dark-100">{mad.talent.name}</dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-light-900">
              {t("mads.startDate")}
            </dt>
            <dd className="mt-1 text-sm text-dark-100">
              {format(new Date(mad.startDate), t("dateFormat") as string)}
            </dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-light-900">
              {t("mads.group")}
            </dt>
            <dd className="mt-1 text-sm text-dark-100">{mad.campaign.name}</dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-light-900">
              {t("mads.endDate")}
            </dt>
            <dd className="mt-1 text-sm text-dark-100">
              {mad.endDate
                ? format(new Date(mad.endDate), t("dateFormat") as string)
                : "N/A"}
            </dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-light-900">
              {t("mads.referent", { count: mad.referents.length })}
            </dt>
            <dd className="mt-1 text-sm text-dark-100">
              {mad.referents.length > 0
                ? mad.referents.map(
                    (referent, index) =>
                      `${referent.name}${
                        index < mad.referents.length - 1 ? ", " : ""
                      }`
                  )
                : t("mads.noReferent")}
            </dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-light-900">
              {t("mads.nextReport")}
            </dt>
            <dd className="mt-1 text-sm text-dark-100">
              {mad.nextReportAt
                ? t("mads.nextReportPlannedFor", {
                    date: format(
                      new Date(mad.nextReportAt),
                      t("dateFormat") as string
                    ),
                  })
                : t("mads.noMoreReports")}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-sm font-medium text-light-900">
              {t("mads.description")}
            </dt>
            <dd className="mt-1 text-sm text-dark-100">
              {mad.missionDescription
                ? mad.missionDescription
                : t("mads.noDescription")}
            </dd>
          </div>
          <UserFollowUpDocuments mad={mad} />
        </dl>
      </div>
    </div>
  );
};

type UserFollowUpScreenProps = RouteComponentProps<{ id: string }>;

const UserFollowUpScreen: React.FC<UserFollowUpScreenProps> = ({ match }) => {
  const t = useTranslations();
  const { data, loading, error } = useGetMadFull({
    variables: { id: match.params.id },
  });

  const mad = data?.mad;

  return (
    <ScreenContainer
      loading={loading}
      error={
        isUnauthorized(error) && (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <WLink variant="button" to="/">
                {t("network.backToHome")}
              </WLink>
            }
          />
        )
      }
    >
      {mad && (
        <>
          <ScreenHeader
            title={mad.title ? mad.title : mad.template?.title}
            description={mad.missionDescription}
            icon={MadIcon}
            actions={[
              mad.canInsert && (
                <WLink
                  variant="button-subtle"
                  to={`/mads/${mad.id}/edit`}
                  id="updateMadLink"
                >
                  {t("mads.update")}
                </WLink>
              ),
              <WLink
                variant="button"
                to={`/templates/${mad.template.id}/edit?madId=${mad.id}`}
                id="updateTemplateLink"
              >
                {t("reports.updateTemplate")}
              </WLink>,
            ]}
          />
          <Container className="px-4 space-y-8 sm:px-6 lg:px-24">
            <UserFollowUpOverdueAlert mad={mad} />
            <UserFollowUpStatus mad={mad} />
            <Tabs>
              <TabList>
                <Tab>{t("mads.summary")}</Tab>
                <Tab>{t("mads.analytics")}</Tab>
                <Tab>{t("mads.reportsTab")}</Tab>
                <Tab>{t("mads.details")}</Tab>
              </TabList>
              <TabPanels>
                <TabPanel className="bg-white rounded-sm">
                  <div className="space-y-8">
                    <UserFollowUpBasicInformations mad={mad} />
                    <EvaluatedSkills mad={mad} />
                  </div>
                  <div>
                    <UserFollowUpContracts mad={mad} />
                  </div>
                </TabPanel>
                <TabPanel className="bg-white rounded-sm">
                  <div className="space-y-8">
                    <UserFollowUpAnalytics mad={mad} />
                  </div>
                </TabPanel>
                <TabPanel className="bg-white rounded-sm">
                  <div className="space-y-8">
                    <UserFollowUpReports mad={mad} />
                  </div>
                </TabPanel>
                <TabPanel className="bg-white rounded-sm">
                  <UserFollowUpActivities mad={mad} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Container>
        </>
      )}
    </ScreenContainer>
  );
};

export default UserFollowUpScreen;
